import React, { useCallback, useEffect, useState } from "react";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Button, ButtonVariantOption } from "@/components/Button";
import { SizingWrapperStyle } from "@/components/Wrapper";

// 取得履歴の常設更新ボタン。
//
// 取得導線（不動産登記簿を取得する）の隣に並べるため、履歴セクション本体（PropertyCodePictureRequestSection）とは
// 独立した自己完結コンポーネント。
// クールダウン中は残秒を「残りN秒」とラベル表示し、リフレッシュアイコンは出さない（他画面の更新ボタンと統一）。

/** 再取得ボタン押下後の連打防止クールダウン（秒）。 */
export const REFETCH_COOLDOWN_SEC = 5;

interface RefetchHistoryButtonProps {
  /** 履歴ローディング中（初回フェッチ進行中）。 */
  isLoading: boolean;
  /** 再検証中（再取得・mutate 進行中）。データ保持中の再フェッチを表す。 */
  isValidating: boolean;
  /** 履歴の再取得（mutateHistory）。失敗時の通知は呼び出し側で行う。 */
  onRefetch: () => void;
}

const RefetchHistoryButtonComponent: React.FC<RefetchHistoryButtonProps> = ({
  isLoading,
  isValidating,
  onRefetch,
}) => {
  // 再取得直後の連打防止クールダウンの残秒（0 で非クールダウン）。
  const [remainingSec, setRemainingSec] = useState(0);

  // 残秒が正のとき 1 秒後に 1 減らす。effect の戻り値で unmount/再押下時に確実にクリアする。
  useEffect(() => {
    if (remainingSec <= 0) return undefined;
    const id = setTimeout(() => {
      setRemainingSec((sec) => sec - 1);
    }, 1000);
    return () => {
      clearTimeout(id);
    };
  }, [remainingSec]);

  const handleRefetch = useCallback(() => {
    onRefetch();
    setRemainingSec(REFETCH_COOLDOWN_SEC);
  }, [onRefetch]);

  const isCoolingDown = remainingSec > 0;
  const isRefetchDisabled = isLoading || isValidating || isCoolingDown;
  const label = isCoolingDown ? `残り${remainingSec}秒` : "更新";
  // クールダウン中は残秒ラベルをスピナーで覆い隠さないよう、再検証スピナーは満了後のみ出す。
  const showValidatingSpinner = isValidating && !isCoolingDown;

  return (
    <Button
      variant={ButtonVariantOption.Outlined}
      label={label}
      startIcon={isCoolingDown ? undefined : <RefreshIcon />}
      size={SizingWrapperStyle.SMALL}
      onClick={handleRefetch}
      disabled={isRefetchDisabled}
      hasLoading
      loading={showValidatingSpinner}
    />
  );
};

/**
 * 取得履歴の更新ボタン。親（PropertyCodeAcquisition）の再レンダリングでボタンが
 * 不要に再描画されるのを防ぐためメモ化する。
 */
export const RefetchHistoryButton = React.memo(RefetchHistoryButtonComponent);
